import {ENTITY_SERVICE_REGISTRY, EntityServiceEntry} from
        "../../../../../core/shared/dto/dto-service/entity-service-registry";
import {CwRequestService} from "../../../service/api/cw-request.service";
import {CwRequestTypeService} from "../../../service/api/cw-request-type.service";
import {
    CwRequestTypeObjectComponent
} from "../object-component/cw-request-type-object-component/cw-request-type-object.component";
import {CwRequestObjectComponent} from "../cw-request-object-component/cw-request-object.component";

/**
 * Registry entries of the T3 Antragseingang family (thirteen aliases, all mirroring the verified
 * {@code /mvsa/cw/…} controllers).
 *
 * <h3>Why this list lives here and not only in cw.entity-provider.ts</h3>
 * The request surface is delivered into TWO shells. The alpha shell loads the whole
 * {@code CwModule}, which provides {@code CW_ENTITY_PROVIDER}; the consultant shell deliberately
 * loads ONLY {@code CwRequestModule} (see its class doc). Keeping the aliases in the shared module
 * means both shells resolve them from the SAME list — a shell can no longer be short of an alias
 * that the other one has.
 *
 * {@code EntityServiceCollector} folds every contribution into one Map by key, so the alpha shell
 * registering these entries twice (once through {@code CW_ENTITY_PROVIDER}, once through
 * {@code CwRequestModule}) is idempotent, not a conflict.
 *
 * <h3>service: is not optional here</h3>
 * {@code MvsCoreService.getCrudService} only builds a GENERIC service when {@code route} starts
 * with a slash. {@code cw.CwRequest} and {@code cw.CwRequestType} carry an FE path
 * ({@code 'cw/request/review'} / {@code 'cw/config'}) instead, so without an explicit
 * {@code service} they resolve to NULL and every metadata-driven form and widget on them renders
 * SILENTLY EMPTY. The two service classes below are the reason the capture page shows fields at
 * all — never drop them to "clean up" the entry.
 *
 * To be precise about WHICH of the two rules applies: {@code getCrudService} checks
 * {@code injectable.service} FIRST and only then falls back to the leading-slash rule. An entry
 * carrying {@code service:} is therefore resolvable REGARDLESS of the route format — the missing
 * slash on {@code 'cw/request/review'} / {@code 'cw/config'} is NOT a defect (the older wording
 * produced exactly that false alarm). The invariant for every entry below is: {@code route} with a
 * leading slash OR an explicit {@code service}.
 *
 * <h3>The extended cw.CwRequestType entry lives HERE and nowhere else</h3>
 * The alpha shell folds this list in TWICE (through {@code CW_ENTITY_PROVIDER} and through
 * {@code CwRequestModule}) and {@code EntityServiceCollector} folds by key — a second, poorer copy
 * of the same alias would win depending on fold order and the object page would load empty. The
 * entry is therefore extended in place and never duplicated into {@code cw.entity-provider.ts}.
 *
 * {@code CwRequestTypeObjectComponent} itself is declared by {@code CwModule}, not by
 * {@code CwRequestModule}: the Antragstyp configuration is a back-office surface and deliberately
 * has no entry point in the consultant shell (no config page and no {@code cw/cwRequestType/:id}
 * route there), so the reference stays inert in that shell.
 */
export const CW_REQUEST_ENTITY_ENTRIES: EntityServiceEntry[] = [
    // objectComponent closes a concrete gap: without it {@code MvsCoreService.getObjectComponent}
    // finds no registry mapping and falls through to the service, where the inherited
    // {@code MvsCrudService.getObjectComponent} returns null — so no mvs-object host and no widget
    // could render an Antragseingang at all. This entry is the ONLY place the mapping may live:
    // CwRequestService deliberately carries no getObjectComponent() override (skill
    // fe_core_entity_registry MIG2). CwRequestObjectComponent is declared by CwRequestModule, the
    // same module that provides these entries, so both shells can instantiate it.
    //
    // objectPageComponent is DELIBERATELY absent: the route 'cw/request/:id' must keep resolving to
    // CwRequestCapturePage. Adding it here would silently replace the capture surface with the
    // object shell.
    ['cw.CwRequest', {
        route: 'cw/request/review',
        objectRoute: 'cw/request/:id',
        objectComponent: [{component: CwRequestObjectComponent}],
        service: CwRequestService
    }],
    // [T3 · P1] Komplexe Konfigurationsentitaet (drei 1:n-Kinder) — eigene Objektseite statt
    // Inline-Master-Detail (CONFIG-003). objectRoute bestimmt die URL, objectComponent den Inhalt,
    // den die objectInplaceDirective der Seite aufloest. BEIDE Schluessel gehoeren in denselben
    // Commit wie die Route 'cw/cwRequestType/:id' in cw.route.ts — mit nur einem davon laedt die
    // Seite leer und meldet nichts.
    ['cw.CwRequestType', {
        route: 'cw/config',
        objectRoute: 'cw/cwRequestType/:id',
        objectComponent: [{component: CwRequestTypeObjectComponent}],
        objectPageComponent: CwRequestTypeObjectComponent,
        service: CwRequestTypeService
    }],
    ['cw.CwRequestTypePartner', {route: '/cw/requestTypePartners'}],
    ['cw.CwRequestTypeDocumentRole', {route: '/cw/requestTypeDocumentRoles'}],
    ['cw.CwRequestTypeTicketType', {route: '/cw/requestTypeTicketTypes'}],
    ['cw.CwRequestRejectReason', {route: '/cw/requestRejectReasons'}],
    ['cw.CwRequestDocument', {route: '/cw/requestDocuments'}],
    ['cw.CwRequestComment', {route: '/cw/requestComments'}],
    ['cw.CwRequestAnalysis', {route: '/cw/requestAnalyses'}],
    ['cw.CwRequestAnalysisResult', {route: '/cw/requestAnalysisResults'}],

    // --- [cw_request_enhancements WP-02] Request satellites: what the document reader found, kept
    // ON THE REQUEST until an explicit take-over step moves it into the case (B-38 / B-30b).
    //
    // Route-only is compliant here: all three are pure generic CRUD (ObjectCrudController, no
    // custom endpoint) and every route starts with a slash, so getCrudService() builds a generic
    // MvsCrudService. MANDATORY registration nonetheless — an unregistered alias resolves to a NULL
    // service and the take-over steps of WP-05/WP-06 would render SILENTLY EMPTY instead of failing.
    //
    // The three aliases live in THIS list, not in cw.entity-provider.ts: the take-over steps run in
    // the alpha shell (which spreads this list through CW_ENTITY_PROVIDER), while the capture surface
    // of the consultant shell loads only CwRequestModule (which provides it directly). Duplicating
    // the literals into cw.entity-provider.ts is what the class doc above forbids.
    //
    // Path spellings are NOT uniform and are copied from the controllers, not guessed:
    // CwRequestPersonController maps /mvsa/cw/cwRequestPersons (the design's longer spelling), while
    // the two object controllers map /mvsa/cw/requestObjects and /mvsa/cw/requestObjectFields.
    ['cw.CwRequestPerson', {route: '/cw/cwRequestPersons'}],
    ['cw.CwRequestObject', {route: '/cw/requestObjects'}],
    ['cw.CwRequestObjectField', {route: '/cw/requestObjectFields'}],
];

/** Multi provider form of {@link CW_REQUEST_ENTITY_ENTRIES}, provided by {@code CwRequestModule}. */
export const CW_REQUEST_ENTITY_PROVIDER = [
    {
        provide: ENTITY_SERVICE_REGISTRY,
        useValue: CW_REQUEST_ENTITY_ENTRIES,
        multi: true
    }
];
